import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ValidationErrorInterface } from '@zaoblako/shared/common/interfaces/validation-error.interface';
import { ExternalUserFindInterface } from '@zaoblako/shared/modules/security/dtos/external-user/external-user-find.dto';
import {
  ExternalUserFormDto,
  ExternalUserFormInterface
} from '@zaoblako/shared/modules/security/dtos/external-user/external-user-form.dto';
import { ProfileFindInterface } from '@zaoblako/shared/modules/security/dtos/profile/profile-find.dto';
import { ProfileFormDto, ProfileFormInterface } from '@zaoblako/shared/modules/security/dtos/profile/profile-form.dto';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { ExternalUserService } from '@zaoblako/web/core/services/security/external-user.service';
import { ProfileService } from '@zaoblako/web/core/services/security/profile.service';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { ClientAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/client-auto-complete/client-auto-complete.component';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { ContragentMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/contragent-multi-select/contragent-multi-select.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { InputMaskModule } from 'primeng/inputmask';
import { forkJoin, Observable } from 'rxjs';

@Component({
  selector: 'crm-external-user-edit-form',
  standalone: true,
  imports: [
    CommonModule,
    SpinnerComponent,
    ButtonModule,
    InputTextComponent,
    JoinPipe,
    CardModule,
    CheckboxModule,
    FormsModule,
    ContragentMultiSelectComponent,
    ClientAutoCompleteComponent,
    InputMaskModule
  ],
  templateUrl: './external-user-edit-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExternalUserEditFormComponent extends BaseFormComponent<ExternalUserFormInterface> implements OnInit {
  @Input() public readonly index: number;

  public user: ExternalUserFormInterface = <ExternalUserFormInterface>{};
  public profile: ProfileFormInterface = <ProfileFormInterface>{};
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public dto = ExternalUserFormDto;
  public engine: 'class-transformer' | 'serde' = 'serde';

  public profileError: ValidationErrorInterface<ProfileFormInterface>;

  public constructor(
    private readonly externalUserService: ExternalUserService,
    private readonly profileService: ProfileService,
    private readonly formStoreService: FormStoreService,
    private readonly errorService: ErrorService,
    private readonly cdRef: ChangeDetectorRef
  ) {
    super();
  }

  public ngOnInit(): void {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.externalUserService
      .findById<ExternalUserFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId)
      .subscribe({
        next: (user) => {
          this.user = user as any;
          this.profile = user.profile;
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public saveUser() {
    this.profileError = null;

    if (this.profile.phone === '') {
      this.profile.phone = null;
    }

    const [, errors] = this.validationService.parseAndValidate(this.profile, ProfileFormDto);

    if (errors) {
      this.profileError = errors.validationErrors;
      return;
    }

    if (this.checkValidate(this.user).valid) {
      this.formStoreService.setLoading(true, this.index);

      forkJoin([
        this.profileService.update<ProfileFormInterface, ProfileFindInterface>(this.profile),
        this.externalUserService.update<ExternalUserFormInterface, ExternalUserFindInterface>(this.user)
      ]).subscribe({
        next: ([profile, user]) => {
          this.formStoreService.setLoading(false, this.index);
          this.errorService.addSuccessMessage(`Внешний пользователь "${profile?.name}" обновлен`);
          this.formStoreService.saveNext(user, this.index);
        },
        error: () => {
          this.formStoreService.setLoading(false, this.index);
        }
      });
    }
  }
}
